import { BaseService } from "./base.service";
import {
	BidCommitteeAppointmentModel,
	BidCommitteeInvitationData,
	IBidCommitteeGridDetail,
	IBidCommitteeMemberGridDetail,
	IBidCommitteeMemberRole,
	IBidCommitteeType,
	IBidCommitteeUser,
	VerifyOtpModel,
} from "models/BidCommittee/BidCommitteeModel";
import {
	BidCommitteeDetailModel,
	BidCommitteeExternalExpertiseModel,
	BidCommitteeMemberDetailAddModel,
	BidCommitteeMemberDetailModel,
} from "models/BidCommittee/BidCommitteeDetailModel";
import { BidCommitteeTableFilterModel } from "models/BidCommittee/BidCommitteeTableFilterModel";
import {
	IBidCommitteeAssignment,
	IBidCommitteeForMeetingEvent,
	IBidCommitteeMember,
} from "models/BidCommittee/BidCommittee";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import { TableFilterModel } from "models/TableFilterModel";

class BidCommitteeServiceInternal extends BaseService {
	getBidCommitteeTypeList = async (): Promise<IBidCommitteeType[]> =>
		this.callApi<IBidCommitteeType[]>(`BidCommitteeType/GetAll`, "GET");

	getBidCommitteeMemberRoleList = async (): Promise<
		IBidCommitteeMemberRole[]
	> =>
		this.callApi<IBidCommitteeMemberRole[]>(
			`BidCommitteeMemberRole/GetAll`,
			"GET"
		);

	getBidCommitteeGridList = async (
		model: BidCommitteeTableFilterModel
	): Promise<IBidCommitteeGridDetail[]> =>
		this.callApi<IBidCommitteeGridDetail[]>(
			`BidCommittee/GetBidCommitteeList`,
			"POST",
			undefined,
			model
		);

	getCommitteeMemberGridList = async (
		model: TableFilterModel
	): Promise<IBidCommitteeMemberGridDetail[]> =>
		this.callApi<IBidCommitteeMemberGridDetail[]>(
			`BidCommitteeMember/GetCommitteeMemberGridList`,
			"POST",
			undefined,
			model
		);

	getBidCommitteeDetail = async (
		bidCommitteeId: number
	): Promise<BidCommitteeDetailModel> =>
		this.callApi<BidCommitteeDetailModel>(
			`BidCommittee/GetById/${bidCommitteeId}`,
			"GET"
		);

	getBidCommitteeAcceptedMemberCount = async (
		bidCommitteeId: number
	): Promise<number> =>
		this.callApi<number>(
			`BidCommittee/AcceptedMembersCount/${bidCommitteeId}`,
			"GET"
		);

	getBidCommitteeMemberDetail = async (
		bidCommitteeMemberId: number
	): Promise<BidCommitteeMemberDetailModel> =>
		this.callApi<BidCommitteeMemberDetailModel>(
			`BidCommitteeMember/GetById/${bidCommitteeMemberId}`,
			"GET"
		);

	saveBidCommitteeDetail = async (
		model: BidCommitteeDetailModel
	): Promise<{ bidCommitteeId: number }> =>
		this.callApi<{ bidCommitteeId: number }>(
			`BidCommittee/Save`,
			"POST",
			undefined,
			model
		);

	// saveBidCommitteeMemberDetail = async (
	// 	model: BidCommitteeMemberDetailModel
	// ): Promise<{ bidCommitteeMemberId: number }> =>
	// 	this.callApi<{ bidCommitteeMemberId: number }>(
	// 		`BidCommitteeMember/Save`,
	// 		"POST",
	// 		undefined,
	// 		model
	// 	); // commenting as this API is not in use for current scenario.

	updateBidCommitteeDetail = async (
		model: BidCommitteeDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommittee/Update/${model.bidCommitteeId}`,
			"PUT",
			undefined,
			model
		);

	updateBidCommitteeMemberDetail = async (
		bidCommitteeMemberId: number,
		model: BidCommitteeMemberDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommitteeMember/Update/${bidCommitteeMemberId}`,
			"PUT",
			undefined,
			model
		);

	deleteBidCommitteeDetail = async (
		bidCommitteeId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommittee/Delete/${bidCommitteeId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	deleteBidCommitteeMemberDetail = async (
		bidCommitteeMemberId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommitteeMember/Delete/${bidCommitteeMemberId}`,
			"DELETE"
		);

	getBidCommitteeListForMeetingEvent = async (
		bidCommitteeTypeId: number
	): Promise<IBidCommitteeForMeetingEvent[]> =>
		this.callApi<IBidCommitteeForMeetingEvent[]>(
			`BidCommittee/GetBidCommitteeListForMeetingEvent/${bidCommitteeTypeId}`,
			"GET"
		);

	getCommitteeMemberList = async (
		bidCommitteeId: number
	): Promise<IBidCommitteeMember[]> =>
		this.callApi<IBidCommitteeMember[]>(
			`BidCommitteeMember/GetCommitteeMemberList/${bidCommitteeId}`,
			"GET"
		);

	checkBidCommitteeAlreadyUsed = async (
		bidCommitteeId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BidCommittee/CheckBidCommitteeAlreadyUsed/${bidCommitteeId}`,
			"GET"
		);

	isBidCommitteeChairPersonExist = async (
		bidCommitteeId: number,
		bidCommitteeMemberId: number
	): Promise<boolean> => {
		let urlParams = {} as any;
		if (bidCommitteeMemberId > 0) {
			urlParams["bidCommitteeMemberId"] = bidCommitteeMemberId.toString();
		}
		return this.callApi<boolean>(
			`BidCommitteeMember/IsBidCommitteeChairPersonExist/${bidCommitteeId}`,
			"GET",
			new URLSearchParams(urlParams)
		);
	};

	getBidCommitteeChairPerson = async (
		model: ITableNumberColumnFilter
	): Promise<IBidCommitteeUser[]> =>
		this.callApi<IBidCommitteeUser[]>(
			`BidCommitteeMember/GetBidCommitteeChairPerson`,
			"POST",
			undefined,
			model
		);

	getBidCommitteeMemberUser = async (
		model: ITableNumberColumnFilter
	): Promise<IBidCommitteeUser[]> =>
		this.callApi<IBidCommitteeUser[]>(
			`BidCommitteeMember/GetBidCommitteeMemberUser`,
			"POST",
			undefined,
			model
		);

	saveBidCommitteeMemberDetail = async (
		model: BidCommitteeMemberDetailAddModel[]
	): Promise<number> =>
		this.callApi<number>(`BidCommitteeMember/Save`, "POST", undefined, model);

	activateBidCommittee = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`BidCommittee/ActivateBidCommittee`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	holdBidCommittee = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`BidCommittee/HoldBidCommittee`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	terminateBidCommittee = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`BidCommittee/TerminateBidCommittee`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	sendAppointment = async (bidCommitteeId: number): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommittee/SendAppointment/${bidCommitteeId}`,
			"POST",
			undefined,
			undefined
		);

	updateMemberStatus = async (
		bidCommitteeMemberId: number,
		statusId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommitteeMember/UpdateMemberStatus/${bidCommitteeMemberId}/${statusId}`,
			"POST",
			undefined,
			undefined
		);

	getDecryptedBidCommitteeMemberId = async (key: string): Promise<number> =>
		this.callApi<number>(
			`BidCommitteeMember/GetDecryptedBidCommitteeMemberId`,
			"POST",
			undefined,
			{
				key: key,
			}
		);

	getBidCommitteeInvitationData = async (
		bidCommitteeMemberId: number
	): Promise<BidCommitteeInvitationData> =>
		this.callApi<BidCommitteeInvitationData>(
			`BidCommitteeMember/GetBidCommitteeInvitationData/${bidCommitteeMemberId}`,
			"GET",
			undefined,
			undefined
		);

	declineAppointment = async (
		model: BidCommitteeAppointmentModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommitteeMember/DeclineAppointment`,
			"POST",
			undefined,
			model
		);

	acceptAppointment = async (
		model: BidCommitteeAppointmentModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidCommitteeMember/AcceptAppointment`,
			"POST",
			undefined,
			model
		);

	eSignature = async (bidCommitteeMemberId: number): Promise<number> =>
		this.callApi<number>(
			`BidCommitteeMember/ESignature/${bidCommitteeMemberId}`,
			"POST",
			undefined,
			undefined
		);

	saveBidCommitteeMemberAttachment = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`BidCommitteeMemberAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	getAttachmentByBidCommitteeMemberId = async (
		bidCommitteeMemberId: number
	): Promise<string> =>
		this.callApi<string>(
			`BidCommitteeMemberAttachment/GetAttachmentByBidCommitteeMemberId/${bidCommitteeMemberId}`,
			"GET"
		);

	getDecryptedBidCommitteeId = async (key: string): Promise<number> =>
		this.callApi<number>(
			`BidCommittee/GetDecryptedBidCommitteeId`,
			"POST",
			undefined,
			{
				key: key,
			}
		);

	getBidCommitteeAssignment = async (): Promise<IBidCommitteeAssignment[]> =>
		this.callApi<IBidCommitteeAssignment[]>(
			`BidCommittee/GetBidCommitteeAssignment`,
			"GET"
		);
	getBidCommitteeExternalExpertiseAll = async (): Promise<
		BidCommitteeExternalExpertiseModel[]
	> =>
		this.callApi<BidCommitteeExternalExpertiseModel[]>(
			"BidCommitteeExternalExpertise/GetAll",
			"GET",
			undefined,
			undefined
		);
	getBidCommitteeExternalExpertiseById = async (
		bidCommitteeExternalExpertiseId: number
	): Promise<BidCommitteeExternalExpertiseModel> =>
		this.callApi<BidCommitteeExternalExpertiseModel>(
			`BidCommitteeExternalExpertise/GetById/${bidCommitteeExternalExpertiseId}`,
			"GET",
			undefined,
			undefined
		);

	saveOrUpdateBidCommitteeExternalExpertise = async (
		payload: BidCommitteeExternalExpertiseModel
	): Promise<number> =>
		this.callApi<number>(
			"BidCommitteeExternalExpertise/SaveUpdate",
			"POST",
			undefined,
			payload,
			undefined
		);

	deleteBidCommitteeExternalExpertise = async (
		bidCommitteeExternalExpertiseId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BidCommitteeExternalExpertise/Delete/${bidCommitteeExternalExpertiseId}`,
			"DELETE",
			undefined,
			undefined
		);
}

export const BidCommitteeService = new BidCommitteeServiceInternal();
